import { createTheme } from '@mui/material/styles';


// Import Avenir font files
import AvenirLight from '@/assets/fonts/Avenir-300.woff2';
import AvenirRegular from '@/assets/fonts/Avenir-400.woff2';
import AvenirMedium from '@/assets/fonts/Avenir-500.woff2';
import AvenirBold from '@/assets/fonts/Avenir-700.woff2';

// Sidebar width constants
export const SIDEBAR_COLLAPSED_WIDTH = 60;
export const SIDEBAR_EXPANDED_WIDTH = 280;

// Extend MUI Typography with custom variant for second overline style
declare module '@mui/material/styles' {
  interface TypographyVariants {
    overlineSecondary: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    overlineSecondary?: React.CSSProperties;
  }

  // Custom color palette extension - OC&C Marketing Palette only
  interface Palette {
    custom: {
      burntOrange: string;
      lightOrange: string;
      lightPeach: string;
      cappuccino: string;
      lightBlue: string;
      darkTeal: string;
      brightTeal: string;
      limeGreen: string;
      deepTeal: string;
      gold: string;
      tableRowStripe: string;
      distribution: {
        bt: string;
        ot: string;
        at: string;
        o: string;
      };
    };
  }

  interface PaletteOptions {
    custom?: {
      burntOrange?: string;
      lightOrange?: string;
      lightPeach?: string;
      cappuccino?: string;
      lightBlue?: string;
      darkTeal?: string;
      brightTeal?: string;
      limeGreen?: string;
      deepTeal?: string;
      gold?: string;
      tableRowStripe?: string;
      distribution?: {
        bt?: string;
        ot?: string;
        at?: string;
        o?: string;
      };
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    overlineSecondary: true;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: '"Nunito Sans", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',

    h6: {
      fontWeight: 400,
    },

    subtitle2: {
      fontWeight: 400,
    },

    button: {
      fontSize: '0.9375rem',
      letterSpacing: '0.02875rem',
      textTransform: 'uppercase',
    },

    // Custom overlineSecondary variant (lowercase, tighter spacing)
    overlineSecondary: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.025rem',
      textTransform: 'none',
    },
  },

  palette: {
    mode: 'light',
    // OC&C Brand Colors
    primary: {
      main: '#1D368E', // OC&C Blue
    },
    secondary: {
      main: '#63277F', // OC&C Purple
    },
    error: {
      main: '#C62828',
    },
    warning: {
      main: '#FF9445', // OC&C Light Orange (from palette)
    },
    success: {
      main: '#2E7D32',
    },
    info: {
      main: '#1976D2',
    },

    // Background
    background: {
      default: '#F1F2F3', // OC&C  Neutral light gray
    },

    // Custom extended palette - ONLY colors from OC&C Marketing Palette
    custom: {
      burntOrange: '#FF5A56', // OC&C Burnt Orange
      lightOrange: '#FF9445', // OC&C Light Orange
      lightPeach: '#FFF3E0',  // Calibration matrix promotion-window background
      cappuccino: '#B9AB97',  // OC&C Cappuccino
      lightBlue: '#43CFF6',   // OC&C Light Blue
      darkTeal: '#009897',    // OC&C Dark Teal
      deepTeal: '#00677D',        // OC&C Teal
      brightTeal: '#99D5D5',  // OC&C Bright Teal
      limeGreen: '#BBBE0B',   // OC&C Lime Green
      gold: '#FDF8E8',        // Light cream for overall rating
      tableRowStripe: 'rgba(151, 187, 255, 0.08)', // Figma State Layers/On Primary Container/Opacity-08
      // Distribution-of-outcomes strip on the calibration matrix.
      // Per Figma — not from the OC&C Marketing Palette.
      distribution: {
        bt: '#E89052', // warm orange
        ot: '#A6C5E8', // pale blue
        at: '#1A6E5C', // dark teal-green
        o:  '#F0BCD8', // light pink
      },
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Avenir';
          font-style: normal;
          font-display: swap;
          font-weight: 300;
          src: url(${AvenirLight}) format('woff2');
        }
        @font-face {
          font-family: 'Avenir';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url(${AvenirRegular}) format('woff2');
        }
        @font-face {
          font-family: 'Avenir';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: url(${AvenirMedium}) format('woff2');
        }
        @font-face {
          font-family: 'Avenir';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: url(${AvenirBold}) format('woff2');
        }
      `,
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
        },
      },
    },





    MuiTable: {
      styleOverrides: {
        root: {
          minWidth: 350,
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& tr:nth-of-type(odd) td': {
            backgroundColor: theme.palette.custom.tableRowStripe,
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          ...theme.typography.overline,
          backgroundColor: theme.palette.custom?.deepTeal,
          color: theme.palette.common.white,
          lineHeight: 1,
          verticalAlign: 'middle',
          height: theme.spacing(3.5),
          padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
        }),
        root: ({ theme }) => ({
          borderBottom: `none`,
          padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
        }),
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit !important',
          '&:hover': {
            color: 'inherit !important',
          },
          '&.Mui-active': {
            color: 'inherit !important',
          },
        },
        icon: {
          color: 'inherit !important',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'underline',
          },
        }),
      },
    },
  },
});

export default theme;
